import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Chip,
  alpha,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { useSubscription, SUBSCRIPTION_LEVELS } from './SubscriptionContext';

const SubscriptionUpgradeModal = ({ open, onClose, onUpgrade }) => {
  const { subscription } = useSubscription();

  const features = {
    FREE: [
      'Basic feed access',
      'Limited publications',
      'Standard support',
    ],
    STANDARD: [
      'All FREE features',
      'More publications',
      'Advanced filtering',
      'Priority support',
    ],
    PREMIUM: [
      'All STANDARD features',
      'Unlimited publications',
      'GPT-powered publications',
      'Premium analytics',
      'Early access to features',
      '24/7 priority support',
    ],
  };

  const getNextTier = () => {
    if (subscription === SUBSCRIPTION_LEVELS.FREE) return SUBSCRIPTION_LEVELS.STANDARD;
    if (subscription === SUBSCRIPTION_LEVELS.STANDARD) return SUBSCRIPTION_LEVELS.PREMIUM;
    return null;
  };

  const nextTier = getNextTier();

  // Always render Dialog but control visibility - prevents portal unmount errors
  return (
    <Dialog
      open={open && !!nextTier}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1d2e',
          color: '#E0E0E0',
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#E0E0E0' }}>
            {nextTier ? `Upgrade to ${nextTier}` : 'Already Premium'}
          </Typography>
          <Button
            onClick={onClose}
            sx={{
              minWidth: 'auto',
              color: 'rgba(255, 255, 255, 0.5)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            mb: 3,
          }}
        >
          Unlock more features and get the most out of your Smart Feed experience.
        </Typography>

        {nextTier ? (
          <Grid container spacing={3}>
            {/* Current Plan */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                }}
              >
                <Typography variant="h6" sx={{ mb: 2, color: '#E0E0E0' }}>
                  Current: {subscription}
                </Typography>
                {features[subscription].map((feature, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                    <CheckIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>

            {/* Next Tier */}
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  border: `2px solid ${nextTier === 'PREMIUM' ? '#8b5cf6' : '#3b82f6'}`,
                  bgcolor: alpha(nextTier === 'PREMIUM' ? '#8b5cf6' : '#3b82f6', 0.1),
                  position: 'relative',
                }}
              >
                <Chip
                  label="Recommended"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -10,
                    right: 16,
                    bgcolor: nextTier === 'PREMIUM' ? '#8b5cf6' : '#3b82f6',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
                <Typography variant="h6" sx={{ mb: 2, color: '#E0E0E0' }}>
                  Upgrade to {nextTier}
                </Typography>
                {features[nextTier].map((feature, index) => (
                  <Box key={index} display="flex" alignItems="center" gap={1} mb={1}>
                    <CheckIcon sx={{ fontSize: '1rem', color: '#10b981' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', py: 4 }}>
            You're already on the highest tier! Enjoy all premium features.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            textTransform: 'none',
          }}
        >
          Remind me later
        </Button>
        {nextTier && (
          <Button
            variant="contained"
            onClick={() => {
              if (onUpgrade) {
                onUpgrade(nextTier);
              }
              onClose();
            }}
            sx={{
              bgcolor: nextTier === 'PREMIUM' ? '#8b5cf6' : '#3b82f6',
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': {
                bgcolor: nextTier === 'PREMIUM' ? '#7c3aed' : '#2563eb',
              },
            }}
          >
            Upgrade Now
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionUpgradeModal;

